import bim_styles from '../styles/bim.module.scss';
import { useContext, useState, useMemo, useEffect } from 'react';
import { Context } from '../Context';
import { Slider } from '@mui/material';
import Badge from '@mui/material/Badge';
import CustomTooltip from './ToolTipInfoCustomMUI';
import Select from 'react-select';
import Zoom from '@mui/material/Zoom';
import { useTranslation } from 'react-i18next';

// media
import { ReactComponent as FilterPlus } from '../media/img/icon-filter-plus.svg';
import { ReactComponent as FilterMinus } from '../media/img/icon-filter-minus.svg';
import { ReactComponent as FilterX } from '../media/img/icon-filter-x.svg';
import { ReactComponent as FilterExclamation } from '../media/img/icon-filter-exclamation.svg';

import { AVAILABILITY_STATUS } from '../constants/availabilityStatus';
import Loading from './Loading';
const { AVAILABLE, SOLD, RESERVED } = AVAILABILITY_STATUS;

function ApartmentsFilter() {
	const {
		isMobile,
		isDesktop,
		displayableApartmentsData,
		isFilterActive,
		projectSetup,
		projectStyle,
		applyFiltersHandler,
		resetAllFiltersHandler,
		restorePreviousFiltersHandler,
		previousFiltersConfig,
		previousFiltersUIState,
		setPreviousFiltersUIState,
		initialFiltersConfig,
		filtersConfig,
		compartmentsDictionary,
		buildingsData,
		filteredApartmentsCount,
		allApartmentsCount,
	} = useContext(Context);

	const { t } = useTranslation();
	// --- UNIT TYPES FILTER ---
	// Wyciągamy typy lokali z id_cad
	const allUnitTypes = useMemo(() => {
		if (!displayableApartmentsData) return [];
		const types = new Set(
			displayableApartmentsData.map((a) => a.id_cad.split('-')[3])
		);
		return Array.from(types);
	}, [displayableApartmentsData]);

	const [selectedUnitTypes, setSelectedUnitTypes] = useState(allUnitTypes);

	// Opcje do selecta
	const unitTypeOptions = allUnitTypes.map((type) => ({
		value: type,
		label: type,
	}));

	// Dodajemy obsługę filtra typów lokali
	useEffect(() => {
		applyFiltersHandler({ unitTypes: selectedUnitTypes });
	}, [selectedUnitTypes]);

	// Function to generate slider style based on whether it's actively filtering
	const getSliderStyle = (isActive) => ({
		color: isActive ? 'var(--color-secondary)' : 'var(--color-emphasis)',
		'&:hover': {
			color: 'var(--color-secondary)',
		},
		'& .MuiSlider-thumb': {
			height: '1.25rem',
			width: '1.25rem',
			borderRadius: 0,
			'&:hover': {
				boxShadow: 'none',
			},
			'&:focus': {
				boxShadow: 'none',
			},
			'&:active': {
				boxShadow: 'none',
			},
			'&.Mui-active': {
				boxShadow: 'none',
			},
			'&.Mui-focusVisible': {
				boxShadow: 'none',
			},
		},
		'& .MuiSlider-track': {
			height: '0.25rem',
		},
		'& .MuiSlider-rail': {
			height: '0.15rem',
		},
	});

	// Function to generate react-select styles based on whether it's actively filtering
	const getMultiSelectStyles = (isActive) => ({
		control: (baseStyles, state) => ({
			...baseStyles,
			borderColor: isActive
				? 'var(--color-secondary)'
				: state.isFocused
				? 'var(--color-secondary)'
				: 'hsl(0, 0%, 80%)',
			borderWidth: isActive ? '2px' : '1px',
			borderRadius: '0px',
			boxShadow: state.isFocused ? '0 0 0 1px var(--color-secondary)' : 'none',
			'&:hover': {
				borderColor: 'var(--color-secondary)',
			},
		}),
				option: (baseStyles, state) => ({
			...baseStyles,
			fontSize: '0.9rem',
			padding: '0.4rem 0.75rem',
			backgroundColor: state.isSelected
				? 'var(--color-secondary)'
				: state.isFocused
				? '#' + projectStyle.__color_secondary + '60'
				: 'transparent',
		}),
		// multiValue: (baseStyles) => ({
		// 	...baseStyles,
		// 	backgroundColor: isActive
		// 		? 'var(--color-secondary-light)'
		// 		: 'hsl(0, 0%, 90%)',
		// }),
		// multiValueLabel: (baseStyles) => ({
		// 	...baseStyles,
		// 	color: isActive ? 'var(--color-emphasis)' : baseStyles.color,
		// }),
	});

	const initialRooms = initialFiltersConfig?.rooms;
	const initialTiers = initialFiltersConfig?.tiers;
	const initialArea = initialFiltersConfig?.area;
	const initialPrice = initialFiltersConfig?.price;
	const initialExposition = initialFiltersConfig?.exposition || [];
	const initialAvailability = initialFiltersConfig?.availability || [];
	const initialInsolation = initialFiltersConfig?.insolation || []; // Already grouped ranges like [[0,3],[3,6]]
	const initialOutdoorAreas = initialFiltersConfig?.supplements || [];
	const initialBuildings = initialFiltersConfig?.buildings;

	const [roomsRange, setRoomsRange] = useState([]);
	const [areaRange, setAreaRange] = useState([]);
	const [priceRange, setPriceRange] = useState([]);
	// 	const [roomsRange, setRoomsRange] = useState(initialRooms);
	// const [areaRange, setAreaRange] = useState(initialArea);
	// const [priceRange, setPriceRange] = useState(initialPrice);
	// const [tiersRange, setTiersRange] = useState(initialTiers);
	// const [insolationRange, setInsolationRange] = useState(initialInsolation);
	const [selectedExpositions, setSelectedExpositions] = useState([]);
	const [strictExpositionMatch, setStrictExpositionMatch] = useState(false);
	const [selectedSupplements, setSelectedSupplements] = useState([]);
	const [strictSupplementsMatch, setStrictSupplementsMatch] = useState(false);

	// Używamy useMemo aby defaultAvailability był stabilny
	const defaultAvailability = useMemo(() => {
		return [
			...(initialAvailability.includes(AVAILABLE) ? [AVAILABLE] : []),
			...(initialAvailability.includes(RESERVED) ? [RESERVED] : []),
		];
	}, [initialAvailability]);

	//For initial values and sets i should use allProcessedApartmentsData,but for filtering and setting currnest filter controler states i should use filteredApartmetsData
	//So that after filtering in one way it should influence other filters: namely disable buttons that fall outside of filtered data range.
	//The problem is that RangeSliders can only have min and max values, not disable some values in between, so they will look like something is off, but they will not be.
	//maybe best scenario is to allow user to set any filter even if no apartments match that filter.
	//I also need to think how to handle reseting filters.

	// const [roomsFilterValues, setRoomsFilterValues] = useState(getRoomsRange(allProcessedApartmentsData));
	// const [priceFilterValues, setPriceFilterValues] = useState(getPriceRange(allProcessedApartmentsData));
	// const [tierFilterValues, setTierFilterValues] = useState(getTiersRange(allProcessedApartmentsData));
	// const [surfaceFilterValues, setSurfaceFilterValues] = useState(getAreaRange(allProcessedApartmentsData));
	// const [expositionFilterValues, setExpositionFilterValues] = useState(getExpositionTypes(allProcessedApartmentsData));

	// const [showFilters, setShowFilters] = useState(false);
	const [expandedAdvancedFiltersView, setExpandedAdvancedFiltersView] =
		useState(false);
	const [expandedBasicFiltersMobile, setExpandedBasicFiltersMobile] =
		useState(false);

	// conditional render of filter controllers based on project setup and data ----------------
	//TODO: consider centralizing this logic in Context
	const areSoldVisible =
		projectSetup.website.are_sold_apartments_visible || true;

	//area there unit with different number of rooms? + are rooms filter enabled in project setup?
	const showRoomsController =
		new Set(displayableApartmentsData.map((apartment) => apartment.rooms))
			.size > 1;

	// are there units on different tiers? + is tiers filter enabled in project setup?
	// const showTiersController = new Set(displayableApartmentsData.map((apartment) => apartment.tiers)).size > 1;

	//are there multiple Buildings/Edifices? + is edifices filter enabled in project setup?
	//are there multiple gates? + is gates filter enabled in project setup?
	//are

	// TODO: move to utils?
	const formatOutdoorAreaName = (id) => {
		return (
			compartmentsDictionary?.compartments?.find((comp) => comp.id === id)
				?.name_pl || id
		);
	};
	// TODO: move to utils
	const formatExpositionName = (direction) => {
		switch (direction) {
			case 'N':
				return t('north_short');
			case 'E':
				return t('east_short');
			case 'S':
				return t('south_short');
			case 'W':
				return t('west_short');
			case 'NE':
				return t('northeast_short');
			case 'NW':
				return t('northwest_short');
			case 'SE':
				return t('southeast_short');
			case 'SW':
				return t('southwest_short');
			default:
				return direction;
		}
	};

	// Wyciągamy wszystkie dostępne typy dostępności z danych (nie z initialAvailability)
	const allAvailabilityTypes = useMemo(() => {
		const types = new Set();
		displayableApartmentsData.forEach((apt) => types.add(apt.availability));
		return Array.from(types);
	}, [displayableApartmentsData]);

	const availabilityOptions = [
		...(allAvailabilityTypes.includes(AVAILABLE)
			? [{ value: AVAILABLE, label: t('available') }]
			: []),
		...(allAvailabilityTypes.includes(RESERVED)
			? [{ value: RESERVED, label: t('reserved') }]
			: []),
		...(areSoldVisible && allAvailabilityTypes.includes(SOLD)
			? [{ value: SOLD, label: t('sold') }]
			: []),
	];

	const selectedAvailability =
		filtersConfig.availability?.map((status) => ({
			value: status,
			label:
				availabilityOptions.find((opt) => opt.value === status)?.label ||
				status,
		})) || [];

	const expositionOptions =
		initialExposition.map((direction) => ({
			value: direction,
			label: formatExpositionName(direction),
		})) || [];

	const outdoorAreasOptions =
		initialOutdoorAreas.map((id) => ({
			value: id,
			// label: supplementsData?.find((s) => s.id === id)?.name || id,
			label: formatOutdoorAreaName(id),
		})) || [];

	const tiersOptions =
		initialTiers?.map((tier) => ({
			value: tier,
			label: tier, //TODO: format label to original name form tiersData
		})) || [];
	const selectedTiers =
		filtersConfig.tiers?.map((tier) => ({
			value: tier,
			label: tier, //TODO: format label to original name form tiersData
		})) || [];

	const insolationOptions =
		initialInsolation?.map((insolation) => ({
			value: insolation,
			label: insolation.join('-') + ' h',
		})) || [];
	const selectedInsolation =
		filtersConfig.insolation?.map((insolation) => ({
			value: insolation,
			label: insolation.join('-') + ' h',
		})) || [];

	const buildingOptions =
		initialBuildings?.map((building) => ({
			value: building,
			label:
				buildingsData?.find((b) => b.id_cad === building)?.name_original ||
				building,
		})) || [];
	const selectedBuildings =
		filtersConfig.buildings?.map((building) => ({
			value: building,
			label:
				buildingsData?.find((b) => b.id_cad === building)?.name_original ||
				building,
		})) || [];

	const has_multi_edifices = buildingsData.length > 1;
	const hasMultipleGates =
		new Set(displayableApartmentsData.map((apartment) => apartment.gate)).size >
		1;

	const { floorplan_insolation: isInsolationEnabled } = projectSetup.apartments;
	//TODO: filters feature overhaul
	// - move filtering logic to Context, this component should only handle UI user settings
	// - by default on desktop show all filters (expanded view), add button to collapse/expand filters view
	// - reset filters button
	// - add indicator how many units match current filter settings out of total units
	// - filters by parameters:
	//   - GENERAL RULES:
	//     - only show filter if there is more than one value in dataset or if no values are met (all units are hidden), hide filter,
	//     - if only one value is met (others are inaccessable - sold or hidden), set that value and disable filter,
	//     - if no values are met (all units are hidden), hide filter
	//     - if filter is disabled, show tooltip info why it is disabled (only one value available, all other units are hidden)
	//     - only show filter if its data was ordered - projectSetup,
	//   - rooms (min-max range slider) but limited slider labels to 5+ rooms: if 5 we display 5, if > 5 we display 5+ and include >= 5

	//   - area (min-max range slider) maybe on slider labels floor min and ciel max to 1m2 precision

	//   - price (min-max range slider) - if prices are visible, maybe on slider labels floor min and ciel max to 10000 currency units precision, and step of 10000 currency units, also shorten label 100 tys. - 1,5 mln, TODO: handle different currency formats, localize 'tys.' and 'mln' to 'k' and 'm'?

	//   - tiers (min-max range slider) - if for some reason only one tier exists, hide this filter, if only there are only apartments on one tier, set that tier and disable slider

	//   - availability (buttons available, reserved, sold) - if sold apartments are accessible, also handle error case if availability data is missing, disable sold button if sold apartments are not accessible, disable other buttons if no such apartments exist

	//   - edifices (buttons for each edifice) - if multiple edifices, but if many edifices consider some other UI element like dropdown? multiselect? still 17 items even in multiselect is too much - any other way?

	//   - special labels (on offer, last units, turnkey, etc) - if such data exists, only render applicable buttons
	//   - exposition (buttons for each direction N E W S) - if such data exists, localize direction labels

	//   - insolation (min-max range slider) - if insolation feature is enabled in project setup and such data exists, floor min and ciel max to 1h precision, step of 1h, on slider labels display min and max in HH godz.

	//   - supplementary areas (buttons for balcony, loggia, terrace, garden) - if such data exists

	//   - can we think of any other useful filters?

	const handleFilterChange = (filterType, newValues) => {
		applyFiltersHandler({ [filterType]: newValues });
	};

	const handleExpositionFilterChange = (direction) => {
		const selectionSet = selectedExpositions.includes(direction)
			? selectedExpositions.filter((d) => d !== direction)
			: [...selectedExpositions, direction];

		const newSelected =
			selectionSet.length !== 0 ? selectionSet : initialExposition;

		setSelectedExpositions(selectionSet);
		applyFiltersHandler({ exposition: newSelected });
	};
	const handleExpositionStrictToggle = (e) => {
		setStrictExpositionMatch(e.target.checked);
		applyFiltersHandler({ ['strictExpositionMatch']: e.target.checked });
	};

	const handleAvailabilitySelectChange = (selectedOptions) => {
		//multiselect version
		const selected = selectedOptions
			? selectedOptions.map((opt) => opt.value)
			: [];

		handleFilterChange(
			'availability',
			selected.length ? selected : defaultAvailability
		);
	};

	const handleSupplementsFilterChange = (supplement) => {
		const selectionSet = selectedSupplements.includes(supplement)
			? selectedSupplements.filter((s) => s !== supplement)
			: [...selectedSupplements, supplement];

		const newSelected =
			selectionSet.length !== 0 ? selectionSet : initialOutdoorAreas;

		// const newSelection =
		//     selectionSet.length === 0
		//     ? !strictSupplementsMatch
		//       ? initialSupplements
		//       : selectionSet
		//         : selectionSet;

		setSelectedSupplements(selectionSet);
		applyFiltersHandler({ supplements: newSelected });
	};
	const handleSupplementsStrictToggle = (e) => {
		setStrictSupplementsMatch(e.target.checked);
		applyFiltersHandler({ ['strictSupplementsMatch']: e.target.checked });
	};

	const formatPriceLabel = (value) => {
		if (value >= 1_000_000) {
			return `${(value / 1_000_000).toLocaleString('pl-PL', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})} mln`;
		}
		return `${(value / 1000).toLocaleString('pl-PL', {
			maximumFractionDigits: 0,
		})} tys.`;
	};

	const handleResetAllFilters = () => {
		// Zapisz lokalne stany UI przed resetem
		const localUIState = {
			selectedExpositions,
			selectedSupplements,
			strictExpositionMatch,
			strictSupplementsMatch,
			selectedUnitTypes,
		};

		resetAllFiltersHandler(localUIState);

		// Resetuj lokalne stany do wartości domyślnych
		setSelectedExpositions([]);
		setSelectedSupplements([]);
		setStrictExpositionMatch(false);
		setStrictSupplementsMatch(false);
		setRoomsRange(initialRooms);
		setAreaRange(initialArea);
		setPriceRange(initialPrice);
		setSelectedUnitTypes(allUnitTypes);
	};

	const handleRestorePreviousFilters = () => {
		restorePreviousFiltersHandler();

		// Przywróć lokalne stany UI
		if (previousFiltersUIState) {
			setSelectedExpositions(previousFiltersUIState.selectedExpositions || []);
			setSelectedSupplements(previousFiltersUIState.selectedSupplements || []);
			setStrictExpositionMatch(
				previousFiltersUIState.strictExpositionMatch || false
			);
			setStrictSupplementsMatch(
				previousFiltersUIState.strictSupplementsMatch || false
			);
			setSelectedUnitTypes(
				previousFiltersUIState.selectedUnitTypes || allUnitTypes
			);
		}

		// Przywróć wartości sliderów z previousFiltersConfig
		if (previousFiltersConfig) {
			setRoomsRange(previousFiltersConfig.rooms || initialRooms);
			setAreaRange(previousFiltersConfig.area || initialArea);
			setPriceRange(previousFiltersConfig.price || initialPrice);
		}

		// Wyczyść zapisane stany po przywróceniu
		setPreviousFiltersUIState(null);
	};

	useEffect(() => {
		if (!initialFiltersConfig) return;
		setRoomsRange(initialFiltersConfig.rooms);
		setAreaRange(initialFiltersConfig.area);
		setPriceRange(initialFiltersConfig.price);
	}, [initialFiltersConfig]);

	if (
		!compartmentsDictionary ||
		!initialFiltersConfig ||
		!filtersConfig ||
		Object.keys(initialFiltersConfig).length === 0 ||
		Object.keys(filtersConfig).length === 0
	) {
		return <Loading />;
	}

	return (
		<div id="unit-filters" className={bim_styles.apartments_filters_block}>
			<div className={bim_styles.filters_frame}>
				<div className={bim_styles.filters_top_bar}>
					<div className={bim_styles.filters_heading}>
						{isDesktop && (
							<h2>
								{t('find_unit')}
								{/*TODO: conditional heading based on unit types {t('find_apartment')} */}
							</h2>
						)}
						{isMobile && (
							<button
								className={`${bim_styles.plan_button} ${bim_styles.button_cta_wide}`}
								onClick={() =>
									setExpandedBasicFiltersMobile(!expandedBasicFiltersMobile)
								}
							>
								{/* <Badge badgeContent={4} color="error"> */}
								<div>{t('expand_filters')}</div>
								{/* </Badge> */}
							</button>
						)}
					</div>
					<div className={bim_styles.filters_basic_segment}>
						{(isDesktop || (isMobile && expandedBasicFiltersMobile)) && (
							<div
								className={`${bim_styles.filters_basic_controllers}`}
								// className={`${bim_styles.filters_basic_controllers} ${
								// 	!expandedAdvancedFiltersView
								// 		? bim_styles.filters_not_expanded
								// 		: ''
								// }`}
							>
								{/* -- ROOMS CONTROLLER ------------------------------------------------------------------------------ */}
								<div
									className={`${bim_styles.filter_container} ${bim_styles.filter_container_slider}`}
								>
									<div
										className={bim_styles.filter_label}
										style={
											filtersConfig.rooms[0] !== initialRooms[0] ||
											filtersConfig.rooms[1] !== initialRooms[1]
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('rooms')}</span>
										<div>
											<span>{t('from')}</span>
											<span>{filtersConfig.rooms[0]}</span>
											<span>{t('to')}</span>
											<span>{filtersConfig.rooms[1]}</span>
										</div>
									</div>
									<Slider
										value={filtersConfig?.rooms || initialRooms || [0, 0]}
										onChange={(event, newValue) => {
											setRoomsRange(newValue);
											handleFilterChange('rooms', newValue);
										}}
										valueLabelDisplay="auto"
										min={initialRooms[0] ?? 0}
										max={initialRooms[1] ?? 0}
										step={1}
										// marks={true}
										sx={getSliderStyle(
											filtersConfig.rooms[0] !== initialRooms[0] ||
												filtersConfig.rooms[1] !== initialRooms[1]
										)}
									/>
								</div>
								{/* -- AREA CONTROLLER ------------------------------------------------------------------------------------- */}
								<div
									className={`${bim_styles.filter_container} ${bim_styles.filter_container_slider}`}
								>
									<div
										className={bim_styles.filter_label}
										style={
											filtersConfig.area[0] !== initialArea[0] ||
											filtersConfig.area[1] !== initialArea[1]
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('surface')}</span>
										<div>
											<span>{t('from')}</span>
											<span>{filtersConfig.area[0]} m</span>
											<div
												style={{
													fontSize: '10px',
													transform: 'translateX(-0.5rem)translateY(-0.25rem)',
												}}
											>
												2
											</div>
											<span>{t('to')}</span>
											<span>{filtersConfig.area[1]} m</span>
											<div
												style={{
													fontSize: '10px',
													transform: 'translateX(-0.5rem)translateY(-0.25rem)',
												}}
											>
												2
											</div>
										</div>
									</div>
									<Slider
										value={filtersConfig?.area || initialArea || [0, 0]}
										onChange={(event, newValue) => {
											setAreaRange(newValue);
											handleFilterChange('area', newValue);
										}}
										valueLabelDisplay="auto"
										min={initialArea[0] ?? 0}
										max={initialArea[1] ?? 0}
										step={1}
										sx={getSliderStyle(
											filtersConfig.area[0] !== initialArea[0] ||
												filtersConfig.area[1] !== initialArea[1]
										)}
									/>
								</div>
								{/* -- PRICE CONTROLLER -------------------------------------------------------------------------------------------- */}
								<div
									className={`${bim_styles.filter_container} ${bim_styles.filter_container_slider}`}
								>
									<div
										className={bim_styles.filter_label}
										style={
											filtersConfig.price[0] !== initialPrice[0] ||
											filtersConfig.price[1] !== initialPrice[1]
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('price')}</span>
										<div>
											<span>{t('from')}</span>
											<span>{formatPriceLabel(filtersConfig.price[0])}</span>
											<span>{t('to')}</span>
											<span>{formatPriceLabel(filtersConfig.price[1])}</span>
											<span>{` zł`}</span>
										</div>
									</div>
									<Slider
										value={filtersConfig?.price || initialPrice || [0, 0]}
										onChange={(event, newValue) => {
											setPriceRange(newValue);
											handleFilterChange('price', newValue);
										}}
										valueLabelDisplay="auto"
										min={initialPrice[0] ?? 0}
										max={initialPrice[1] ?? 0}
										step={10000}
										sx={getSliderStyle(
											filtersConfig.price[0] !== initialPrice[0] ||
												filtersConfig.price[1] !== initialPrice[1]
										)}
									/>
								</div>
							</div>
						)}
						{isDesktop && (
							// <Badge badgeContent={isFilterActive ? filteredApartmentsCount : null} color="error">
							<button
								title="Pokaż/ukryj filtry"
								className={`${bim_styles.plan_button__cta} ${bim_styles.plan_button} ${bim_styles.plan_button__normal}`}
								onClick={() =>
									setExpandedAdvancedFiltersView(!expandedAdvancedFiltersView)
								}
							>
								<FilterPlus />
							</button>
							// </Badge>
						)}
						{isMobile && expandedBasicFiltersMobile && (
							<>
								<button
									className={`${bim_styles.plan_button} ${bim_styles.button_cta_wide}`}
									onClick={() =>
										setExpandedAdvancedFiltersView(!expandedAdvancedFiltersView)
									}
								>
									{expandedAdvancedFiltersView
										? t('collapse_advanced_filters')
										: t('expand_advanced_filters')}
								</button>
								<button
									className={`${bim_styles.plan_button} ${bim_styles.button_cta_wide}`}
									onClick={() =>
										setExpandedAdvancedFiltersView(!expandedAdvancedFiltersView)
									}
								>
									{t('hide_all_filters')}
								</button>
							</>
						)}
					</div>
				</div>
				{expandedAdvancedFiltersView && (
					<div className={bim_styles.filters_advanced_segment}>
						<h3>{t('advanced_filters')}</h3>
						<div className={bim_styles.filters_advanced_container}>
							{/* -- TIER CONTROLLER (multiselect) ------------------------------------------------------ */}
							<div className={`${bim_styles.filter_container}`}>
								<div
									className={bim_styles.filter_label}
									style={
										filtersConfig.tiers?.length !== initialTiers?.length ||
										!filtersConfig.tiers?.every((t) =>
											initialTiers?.includes(t)
										)
											? { color: 'var(--color-secondary)', fontWeight: 'bold' }
											: {}
									}
								>
									<span>{t('tier')}</span>
								</div>
								<Select
									isMulti
									value={selectedTiers}
									options={tiersOptions}
									onChange={(selectedOptions) => {
										const selected = selectedOptions
											? selectedOptions.map((opt) => opt.value)
											: [];
										handleFilterChange(
											'tiers',
											selected.length ? selected : initialTiers
										);
									}}
									styles={getMultiSelectStyles(
										filtersConfig.tiers?.length !== initialTiers?.length ||
											!filtersConfig.tiers?.every((t) =>
												initialTiers?.includes(t)
											)
									)}
									placeholder="Wybierz piętra..."
									noOptionsMessage={() => t('no_options')}
								/>
							</div>
							{/* -- INSOLATION CONTROLLER (multiselect) ------------------------------------------------------ */}
							{isInsolationEnabled && (
								<div className={bim_styles.filter_container}>
									<div
										className={bim_styles.filter_label}
										style={
											filtersConfig.insolation?.length !==
												initialInsolation?.length ||
											!filtersConfig.insolation?.every(
												(ins, idx) =>
													ins[0] === initialInsolation[idx]?.[0] &&
													ins[1] === initialInsolation[idx]?.[1]
											)
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('insolation_hours')}</span>
									</div>
									<Select
										isMulti
										value={selectedInsolation}
										options={insolationOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											handleFilterChange(
												'insolation',
												selected.length ? selected : initialInsolation
											);
										}}
										styles={getMultiSelectStyles(
											filtersConfig.insolation?.length !==
												initialInsolation?.length ||
												!filtersConfig.insolation?.every(
													(ins, idx) =>
														ins[0] === initialInsolation[idx]?.[0] &&
														ins[1] === initialInsolation[idx]?.[1]
												)
										)}
										placeholder={`${t('pick_insolation_ranges')}`}
										noOptionsMessage={() => t('no_options')}
									/>
								</div>
							)}
							{/* -- EXPOSITION CONTROLLER ------------------------------------------------------ */}
							<div className={`${bim_styles.filter_container}`}>
								<div
									className={bim_styles.filter_label}
									style={
										(selectedExpositions.length > 0 &&
											(selectedExpositions.length !==
												initialExposition.length ||
												!selectedExpositions.every((exp) =>
													initialExposition.includes(exp)
												))) ||
										strictExpositionMatch
											? { color: 'var(--color-secondary)', fontWeight: 'bold' }
											: {}
									}
								>
									<span>{t('exposition')}</span>
									<div>
										<CustomTooltip
											title={t('strict_match_tooltip_exp')}
											slots={{
												transition: Zoom,
											}}
											arrow
										>
											<span className={bim_styles.filter_label_small}>
												{t('strict_match')}
												{` `}
											</span>
										</CustomTooltip>
										<input
											type="checkbox"
											checked={strictExpositionMatch}
											onChange={(e) => handleExpositionStrictToggle(e)}
										/>
									</div>
								</div>
								<Select
									isMulti
									value={expositionOptions.filter((opt) =>
										selectedExpositions.includes(opt.value)
									)}
									options={expositionOptions}
									onChange={(selectedOptions) => {
										const selected = selectedOptions
											? selectedOptions.map((opt) => opt.value)
											: [];
										setSelectedExpositions(selected);
										handleFilterChange(
											'exposition',
											selected.length ? selected : initialExposition
										);
									}}
									styles={getMultiSelectStyles(
										(selectedExpositions.length > 0 &&
											(selectedExpositions.length !==
												initialExposition.length ||
												!selectedExpositions.every((exp) =>
													initialExposition.includes(exp)
												))) ||
											strictExpositionMatch
									)}
									placeholder={`${t('pick_cardinals')}`}
									noOptionsMessage={() => t('no_options')}
								/>
							</div>
							{/* -- OUTDOOR AREAS CONTROLLER ------------------------------------------------------ */}
							<div className={`${bim_styles.filter_container}`}>
								<div
									className={bim_styles.filter_label}
									style={
										(selectedSupplements.length > 0 &&
											(selectedSupplements.length !==
												initialOutdoorAreas.length ||
												!selectedSupplements.every((sup) =>
													initialOutdoorAreas.includes(sup)
												))) ||
										strictSupplementsMatch
											? { color: 'var(--color-secondary)', fontWeight: 'bold' }
											: {}
									}
								>
									<span>{t('supplements')}</span>
									<div>
										<CustomTooltip
											title={t('strict_match_tooltip_sup')}
											slots={{
												transition: Zoom,
											}}
											arrow
										>
											<span className={bim_styles.filter_label_small}>
												{t('strict_match')}
											</span>
										</CustomTooltip>
										<input
											type="checkbox"
											checked={strictSupplementsMatch}
											onChange={(e) => handleSupplementsStrictToggle(e)}
										/>
									</div>
								</div>
								<Select
									isMulti
									value={outdoorAreasOptions.filter((opt) =>
										selectedSupplements.includes(opt.value)
									)}
									options={outdoorAreasOptions}
									onChange={(selectedOptions) => {
										const selected = selectedOptions
											? selectedOptions.map((opt) => opt.value)
											: [];
										setSelectedSupplements(selected);
										handleFilterChange(
											'supplements',
											selected.length ? selected : initialOutdoorAreas
										);
									}}
									styles={getMultiSelectStyles(
										(selectedSupplements.length > 0 &&
											(selectedSupplements.length !==
												initialOutdoorAreas.length ||
												!selectedSupplements.every((sup) =>
													initialOutdoorAreas.includes(sup)
												))) ||
											strictSupplementsMatch
									)}
									placeholder={`${t('show_units_with')} ...`}
									noOptionsMessage={() => t('no_options')}
								/>
							</div>
							{/* -- BUILDINGS CONTROLLER ------------------------------------------------------ */}
							{has_multi_edifices && (
								<div className={`${bim_styles.filter_container}`}>
									<div
										className={bim_styles.filter_label}
										style={
											filtersConfig.buildings?.length !==
												initialBuildings?.length ||
											!filtersConfig.buildings?.every((b) =>
												initialBuildings?.includes(b)
											)
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('buildings')}</span>
									</div>
									<Select
										isMulti
										value={selectedBuildings}
										options={buildingOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											handleFilterChange(
												'buildings',
												selected.length ? selected : initialBuildings
											);
										}}
										styles={getMultiSelectStyles(
											filtersConfig.buildings?.length !==
												initialBuildings?.length ||
												!filtersConfig.buildings?.every((b) =>
													initialBuildings?.includes(b)
												)
										)}
										placeholder="Wybierz budynki..."
										noOptionsMessage={() => t('no_options')}
									/>
								</div>
							)}
							{/* -- AVAILABILITY CONTROLLER ------------------------------------------------------ */}
							<div className={`${bim_styles.filter_container}`}>
								<div
									className={bim_styles.filter_label}
									style={
										filtersConfig.availability?.length !==
											defaultAvailability.length ||
										!filtersConfig.availability?.every((stat) =>
											defaultAvailability.includes(stat)
										)
											? { color: 'var(--color-secondary)', fontWeight: 'bold' }
											: {}
									}
								>
									<span>{t('availability')}</span>
								</div>
								<Select
									isMulti
									value={selectedAvailability}
									options={availabilityOptions}
									onChange={handleAvailabilitySelectChange}
									isOptionDisabled={(opt) =>
										!allAvailabilityTypes.includes(opt.value)
									}
									styles={getMultiSelectStyles(
										filtersConfig.availability?.length !==
											defaultAvailability.length ||
											!filtersConfig.availability?.every((stat) =>
												defaultAvailability.includes(stat)
											)
									)}
									noOptionsMessage={() => t('no_options')}
								/>
							</div>
							{/* -- UNIT TYPE FILTER ------------------------------------------------------ */}
							{allUnitTypes.length > 1 && (
								<div className={`${bim_styles.filter_container}`}>
									<div
										className={bim_styles.filter_label}
										style={
											selectedUnitTypes.length !== allUnitTypes.length ||
											!selectedUnitTypes.every((type) =>
												allUnitTypes.includes(type)
											)
												? {
														color: 'var(--color-secondary)',
														fontWeight: 'bold',
												  }
												: {}
										}
									>
										<span>{t('unit_type')}</span>
									</div>
									<Select
										isMulti
										value={unitTypeOptions.filter((opt) =>
											selectedUnitTypes.includes(opt.value)
										)}
										options={unitTypeOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: allUnitTypes;
											setSelectedUnitTypes(
												selected.length ? selected : allUnitTypes
											);
										}}
										styles={getMultiSelectStyles(
											selectedUnitTypes.length !== allUnitTypes.length ||
												!selectedUnitTypes.every((type) =>
													allUnitTypes.includes(type)
												)
										)}
										className={bim_styles.multiselect}
										placeholder="Wybierz typ lokalu..."
										noOptionsMessage={() => t('no_options')}
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			<div className={bim_styles.filters_summary_segment}>
				<span>
					{`Liczba widocznych lokali: ${filteredApartmentsCount} / ${allApartmentsCount} 
					`}
				</span>

				<button
					className={`${bim_styles.plan_button} ${bim_styles.plan_button_text}`}
					onClick={() => handleResetAllFilters()}
					disabled={!isFilterActive}
				>
					{'Resetuj wszystkie filtry'}
				</button>
				{/* <button
					className={`${bim_styles.plan_button} ${bim_styles.moreFiltersButton}`}
					onClick={() => {
						if (previousFiltersConfig && !isFilterActive) {
							handleRestorePreviousFilters();
						} else {
							handleResetAllFilters();
						}
					}}
					disabled={!isFilterActive && !previousFiltersConfig}
				>
					{(previousFiltersConfig && !isFilterActive)
						? 'Przywróć poprzednie filtry'
						: 'Resetuj wszystkie filtry'}
				</button> */}
			</div>
		</div>
	);
}

export default ApartmentsFilter;
